import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import type { JourneyStep } from '../../types/klanttraject';

interface Props {
  step: JourneyStep | null;
  onClose: () => void;
  onSaved: () => void;
}

const SKIP_CHECKS = [
  'skip_check_first_blog',
  'skip_check_has_social',
  'skip_check_has_post',
  'skip_check_has_contacts',
  'skip_check_has_sent_campaign',
  'skip_check_logo_mail_dependent',
  'skip_check_no_website',
];

export function StepEditModal({ step, onClose, onSaved }: Props) {
  const { showToast } = useToast();
  const [form, setForm] = useState<Partial<JourneyStep>>(step ?? {
    type: 'mail',
    delay_unit: 'business_day',
    delay_anchor: 'prev_step_completed',
    delay_amount: 1,
    is_active: true,
    description: '',
  });
  const [templates, setTemplates] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('email_templates').select('template_type');
      if (!cancelled) setTemplates((data ?? []).map((t: any) => t.template_type as string).sort());
    })();
    return () => { cancelled = true; };
  }, []);

  async function save() {
    if (!form.step_key || !form.title) {
      showToast('step_key en title zijn verplicht', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload: any = {
        step_order: form.step_order ?? 0,
        type: form.type,
        delay_amount: form.delay_amount ?? 0,
        delay_unit: form.delay_unit,
        delay_anchor: form.delay_anchor,
        fixed_send_time: form.fixed_send_time || null,
        email_template: form.email_template || null,
        script_name: form.script_name || null,
        skip_check: form.skip_check || null,
        title: form.title,
        description: form.description ?? '',
        support_notes: form.support_notes || null,
        is_active: form.is_active ?? true,
      };
      if (step) {
        const { error } = await supabase.from('journey_steps').update(payload).eq('id', step.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('journey_steps').insert({ ...payload, step_key: form.step_key });
        if (error) throw error;
      }
      showToast('Stap opgeslagen', 'success');
      onSaved();
      onClose();
    } catch (e: any) {
      showToast('Opslaan mislukt', 'error', e.message);
    } finally {
      setSaving(false);
    }
  }

  function setF<K extends keyof JourneyStep>(key: K, value: JourneyStep[K] | null) {
    setForm(p => ({ ...p, [key]: value as any }));
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h2 className="text-base font-semibold">{step ? 'Stap bewerken' : 'Nieuwe stap'}</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto text-sm">
          <Field label="step_key (uniek, technisch)">
            <input value={form.step_key ?? ''} onChange={e => setF('step_key', e.target.value)} disabled={!!step} className="w-full px-3 py-1.5 border border-slate-300 rounded font-mono text-xs disabled:bg-slate-100" />
          </Field>
          <Field label="step_order">
            <input type="number" value={form.step_order ?? 0} onChange={e => setF('step_order', parseInt(e.target.value || '0'))} className="w-full px-3 py-1.5 border border-slate-300 rounded" />
          </Field>
          <Field label="title">
            <input value={form.title ?? ''} onChange={e => setF('title', e.target.value)} className="w-full px-3 py-1.5 border border-slate-300 rounded" />
          </Field>
          <Field label="description">
            <textarea value={form.description ?? ''} onChange={e => setF('description', e.target.value)} rows={2} className="w-full px-3 py-1.5 border border-slate-300 rounded" />
          </Field>
          <Field label="type">
            <select value={form.type ?? 'mail'} onChange={e => setF('type', e.target.value as any)} className="w-full px-3 py-1.5 border border-slate-300 rounded">
              <option value="script">script</option>
              <option value="mail">mail</option>
              <option value="nudge_mail">nudge_mail</option>
              <option value="manual_review">manual_review</option>
              <option value="personal_mail_placeholder">personal_mail_placeholder</option>
              <option value="event_celebration">event_celebration</option>
            </select>
          </Field>
          <div className="grid grid-cols-3 gap-2">
            <Field label="delay_amount">
              <input type="number" value={form.delay_amount ?? 0} onChange={e => setF('delay_amount', parseInt(e.target.value || '0'))} className="w-full px-3 py-1.5 border border-slate-300 rounded" />
            </Field>
            <Field label="delay_unit">
              <select value={form.delay_unit ?? 'business_day'} onChange={e => setF('delay_unit', e.target.value as any)} className="w-full px-3 py-1.5 border border-slate-300 rounded">
                <option value="minute">minute</option>
                <option value="hour">hour</option>
                <option value="business_day">business_day</option>
                <option value="calendar_day">calendar_day</option>
              </select>
            </Field>
            <Field label="delay_anchor">
              <select value={form.delay_anchor ?? 'prev_step_completed'} onChange={e => setF('delay_anchor', e.target.value as any)} className="w-full px-3 py-1.5 border border-slate-300 rounded">
                <option value="prev_step_completed">prev_step_completed</option>
                <option value="wizard_completed">wizard_completed</option>
              </select>
            </Field>
          </div>
          <Field label="fixed_send_time (HH:MM, optioneel)">
            <input value={form.fixed_send_time ?? ''} onChange={e => setF('fixed_send_time', e.target.value || null)} placeholder="09:00" className="w-full px-3 py-1.5 border border-slate-300 rounded" />
          </Field>
          <Field label="email_template">
            <select value={form.email_template ?? ''} onChange={e => setF('email_template', e.target.value || null)} className="w-full px-3 py-1.5 border border-slate-300 rounded">
              <option value="">(geen)</option>
              {templates.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="script_name">
            <input value={form.script_name ?? ''} onChange={e => setF('script_name', e.target.value || null)} placeholder="script-..." className="w-full px-3 py-1.5 border border-slate-300 rounded font-mono text-xs" />
          </Field>
          <Field label="skip_check">
            <select value={form.skip_check ?? ''} onChange={e => setF('skip_check', e.target.value || null)} className="w-full px-3 py-1.5 border border-slate-300 rounded">
              <option value="">(geen)</option>
              {SKIP_CHECKS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="support_notes">
            <textarea value={form.support_notes ?? ''} onChange={e => setF('support_notes', e.target.value || null)} rows={2} className="w-full px-3 py-1.5 border border-slate-300 rounded" />
          </Field>
          <Field label="">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_active ?? true} onChange={e => setF('is_active', e.target.checked)} />
              <span>Actief</span>
            </label>
          </Field>
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            Wijzigingen gelden alleen voor nieuwe users. Lopende users zien hun bestaande planning.
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm hover:bg-slate-100 rounded">Annuleren</button>
          <button onClick={save} disabled={saving} className="px-3 py-1.5 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded disabled:opacity-50">{saving ? 'Opslaan...' : 'Opslaan'}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      {label && <label className="block text-xs text-slate-600 mb-1">{label}</label>}
      {children}
    </div>
  );
}
